import { JsonApiObject } from 'json-api-normalizer';

import { AvailableLocale } from '../../shared/intl/types';
import { PaginationFromAPI } from '../discussion/types';

export type PhoneCallStatus = 'connected' | 'connecting' | 'disconnected';

export type PhoningMarkets = AvailableLocale | 'se' | 'dk' | 'nl' | 'ch' | 'at';

// Call recording and transcription types
export type TimestampedWord = {
    word: string;
    start: number;
    end: number;
};

export type PhoneCall = {
    id: number;
    dossier_phone_call_id: string;
    date: Date;
    sendDate: Date;
    duration: number;
    state: number;
    raw_transcription: string | null;
    summary: string;
    timestamped_transcript: TimestampedWord[];
    audio_file: string;
    transcript_error_code: string | null;
    summary_error_code: string | null;
    transcript_deleted_at: Date | null;
    recording_audio_file_deleted_at: Date | null;
};

export type JsonApiResponse<T> = {
    jsonapi: {
        version: string;
    };
    data: JsonApiResource<T>[];
};

type JsonApiResource<T> = {
    type: string;
    id: string;
    attributes: T;
};

export type PhoneCallTokenFromAPI = {
    type: 'dossier-make-phone-call';
    id: string;
    attributes: {
        token: string;
        phoneCallId: number;
    };
};

export interface PhoneCallTokenJSONAPIResponse extends JsonApiObject {
    data: PhoneCallTokenFromAPI;
}
export type PhoneCallToken = { token: string; phoneCallId: number };

export type PhoneCallApi = {
    id: number;
    date: string;
    send_date: string;
    duration: number;
    state: number;
    raw_transcription: string | null;
    summary: string;
    timestamped_transcript: TimestampedWord[];
    audio_file: string;
};

export type DossierPhoneCallApi = {
    dossierId: number;
    phoneCallId: number;
    dossierPhoneCallId: string;
    agentId: number;
    recordingId: string | null;
    transcriptId: string | null;
    transcript: string | null;
    timestampedTranscript: TimestampedWord[] | null;
    language: string | null;
    summary: string | null;
    recordingAudioFileUrl: string | null;
    phoneCallDuration: number | null;
    phoneCallCreatedAt: string;
    recordingDuration: number | null;
    transcriptErrorCode: string | null;
    summaryErrorCode: string | null;
    transcriptDeletedAt: string | null;
    recordingAudioFileDeletedAt: string | null;
};

export interface GetPhoneCallAPIResponse {
    data: PhoneCallApi[];
    embeds: ['quotesHasPhonecalls'];
    pagination: PaginationFromAPI;
}

export const PHONE_CALL_TRANSCRIPT_WEBSOCKET_ACTIONS = {
    RECORDING_ASSIGNED: 'phoning.phone_call_recording_assigned',
    TRANSCRIPT_REGENERATION_REQUESTED: 'phoning.phone_call_transcript_regeneration_requested',
    SUMMARY_REGENERATION_REQUESTED: 'phoning.phone_call_summary_regeneration_requested',
    TRANSCRIPTION_FAILED: 'phoning.phone_call_transcription_failed',
    SUMMARIZATION_FAILED: 'phoning.phone_call_summarization_failed',
    SUMMARIZATION_COMPLETED: 'phoning.phone_call_summarization_completed',
} as const;

export type PhoneCallTranscriptWebSocketAction =
    (typeof PHONE_CALL_TRANSCRIPT_WEBSOCKET_ACTIONS)[keyof typeof PHONE_CALL_TRANSCRIPT_WEBSOCKET_ACTIONS];

export type PhoneCallTranscriptWebSocketEventAttributes = DossierPhoneCallApi & {
    action: PhoneCallTranscriptWebSocketAction;
};

export type PhoneCallTranscriptWebSocketEvent = {
    jsonapi: {
        version: string;
    };
    data: {
        type: 'dossier-phone-call';
        id: string;
        attributes: PhoneCallTranscriptWebSocketEventAttributes;
    };
};

export type TranscriptEventType =
    | 'call-transcription-started'
    | 'call-transcription-ended'
    | 'call-transcription-failed'
    | 'call-summary-started'
    | 'call-summary-ended';

export type PhoneCallSummaryMailFromAPI = {
    content: string;
};

export type PhoneCallSummaryMailJSONAPIResponse = {
    jsonapi: {
        version: string;
    };
    data: {
        type: 'dossier-phone-call-summary-mail';
        id: string;
        attributes: PhoneCallSummaryMailFromAPI;
    };
};

export type PhoneCallSummaryMail = {
    content: string;
};
